import { FC, ReactNode, createContext, memo, useContext, useEffect, useState } from 'react';

import * as Style from '../../styles/context/styles.modal';
import { useEventContext } from './event.context';
import { useLoaderScreenContext } from './loader-screen.context';
import { useMembershipContext } from './membership.context';

export interface ModalContextProps {
    setContentModal: (content: React.ReactNode) => void;
    toggleModal: ({ open }: {open: boolean}) => void;
    defineTitleModal: (title: string) => void;
    handleCustomSizeModal: ({ width, height }: {width: number, height: number}) => void;
    showModal: boolean;
    modalIsOpen: boolean;
    shouldRender: boolean;
    titleModal: string | null;
}

const ModalContext = createContext({} as ModalContextProps);

const ModalProvider: FC<{children: ReactNode}> = memo(({ children }) => {
  const [showModal, setShowModal] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [titleModal, setTitleModal] = useState<string | null>(null);
  const [childrenModal, setChildrenModal] = useState<ReactNode>(null);
  const [customSizeModal, setCustomSizeModal] = useState<{width: number, height: number} | null>(null);

  const { couponApplied } = useEventContext();
  const { fetchingMembership } = useMembershipContext();
  const { showLoader } = useLoaderScreenContext();

  function handleCustomSizeModal({ width, height }: {width: number, height: number}) {
    setCustomSizeModal({ width, height });
  }

  /**
   * Toggles the visibility of the modal.
  */
  const toggleModal = ({ open }: {open: boolean}) => {
    if ((fetchingMembership || showLoader) && showModal && !open) return;
    setShowModal(open);
  };

  /**
   * Sets the content of the modal.
   * @param content - The content to be displayed in the modal.
  */
  const setContentModal = (content: ReactNode) => setChildrenModal(content);

  /**
   * Defines the title of the modal.
   * @param title - The title to be set for the modal.
  */
  const defineTitleModal = (title: string) => setTitleModal(title);

  const values: ModalContextProps = {
    setContentModal, toggleModal, defineTitleModal, handleCustomSizeModal, showModal,
    modalIsOpen : showModal, shouldRender, titleModal
  };

  /**
   * useEffect hook to control the rendering of the modal based on its visibility.
   * This hook runs whenever the `showModal` state changes and performs the following actions:
   * - If the modal is shown (`showModal` is true), it sets `shouldRender` to true immediately.
   * - If the modal is hidden (`showModal` is false), it sets a timeout of 480 milliseconds to:
   *   - Set `shouldRender` to false.
   *   - Clear the content of the modal (`setChildrenModal(null)`).
   *   - Clear the title of the modal (`setTitleModal(null)`).
   * The timeout ensures a smooth transition before completely unmounting the modal content.
   * @param showModal - The boolean state indicating whether the modal is visible or not.
  */
  useEffect(() => {
    if (showModal) return setShouldRender(true);
    setTimeout(() => {
      setShouldRender(false);
      setChildrenModal(null);
      setTitleModal(null);
    }, 480);

    if (!showModal) {
      setTimeout(() => {
        setCustomSizeModal(null);
      }, 500);
    }
  }, [showModal]);

  /**
   * useEffect hook to handle the 'Escape' key press to close the modal.
   * This hook runs whenever the `toggleModalInfo` function changes and performs the following actions:
   * - Adds an event listener to the document for the 'keydown' event.
   * - The event listener checks if the pressed key is 'Escape' and if the modal is shown (`showModal`).
   *   - If both conditions are met, it calls the `toggleModalInfo` function to toggle the modal visibility.
   * - Cleans up the event listener when the component unmounts or when `toggleModalInfo` changes.
   * @param toggleModalInfo - The function to toggle the visibility of the modal.
  */
  useEffect(() => {
    const handleKeyDown = (event: { key: string; }) =>
      (event.key === 'Escape' && showModal) && toggleModal({ open : !showModal });
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleModal]);

  return(
    <ModalContext.Provider value={values}>
      <Style.StyledWrapperModal
        $isVisible={showModal}
        $shouldRender={shouldRender}
      >
        <Style.StyledContentModal
          id="modal-content"
          $isVisible={showModal}
          $extendsBottomPadding={couponApplied?.isValid}
          $customSizeModal={customSizeModal}
        >

          {
            titleModal && (
              <Style.StyledHeaderModal>
                <Style.StyledTitleModal>{ titleModal }</Style.StyledTitleModal>
                <Style.StyledCloseModal onClick={() => toggleModal({ open : !showModal })} />
              </Style.StyledHeaderModal>
            )
          }

          <Style.StyledModalBody>
            { childrenModal }
          </Style.StyledModalBody>

        </Style.StyledContentModal>
      </Style.StyledWrapperModal>
      {children}
    </ModalContext.Provider>
  );

});

ModalProvider.displayName = 'ModalProvider';
export const useModalContext = (): ModalContextProps => useContext(ModalContext);

export { ModalContext, ModalProvider };
